import { Colors, FlexBox } from '@helloself/hello-self-ui';
import axios from 'axios';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useAppSelector } from '../../../app/hooks';
import { selectUser } from '../../auth/selectors';
import { useGetConnectionsForUserQuery } from '../../connections/model/query/connection';
import { selectConnectionsProfileByExpertId } from '../../connections/model/slice/selectors';
import { ProfileProps } from '../containers/ProfilePageRoute';
import { useGetProfilePhotoQuery, usePutProfileMutation, usePutProfilePhotoMutation } from '../model/profile';
import {
    FullProfile,
    isAmplifyBaseQueryError,
    isErrorWithMessage,
    ProfilePhoto,
    PutSuccessResponse,
} from '../model/types';
import { EditModal } from './edit';
import useProfileRecordEvent from './hooks/useProfileRecordEvent';
import { AboutMeTile } from './tiles/about-me/AboutMeTile';
import { BasicInfoTile } from './tiles/basic-info/BasicInfoTile';
import { ConnectionsTile } from './tiles/connections/ConnectionsTile';
import { TopSpecialitiesTile } from './tiles/top-specialities/TopSpecialitiesTile';
import { Toolbar } from './Toolbar';
import { getChangedProfileValues } from './utils';

export default function PartnerProfile({ profile, isLoadingProfiles }: ProfileProps) {
    const edit = new URLSearchParams(window.location.search).get('edit');
    const location = useLocation();
    const [isEditOpen, setIsEditOpen] = useState(edit !== null);
    const [profilePicturePreview, setProfilePicturePreview] = useState('');
    const { cognitoId: expertId } = useParams<{ cognitoId: string }>();
    const connectionsProfile = useAppSelector((state) => selectConnectionsProfileByExpertId(state, expertId));
    const { recordProfileEvent } = useProfileRecordEvent();

    // eslint-disable-next-line prefer-const
    let { data: profilePicture, error: errorLoadingProfilePhoto } = useGetProfilePhotoQuery(expertId, {
        skip: !expertId,
        refetchOnMountOrArgChange: true, // forcing the query to always fetch when this component is mounted
    });

    const { data: connections, isLoading: isLoadingConnections } = useGetConnectionsForUserQuery(expertId);

    const [putPhoto] = usePutProfilePhotoMutation();
    const [putProfile, { error: putProfileError }] = usePutProfileMutation();
    const currentExpert = useAppSelector(selectUser);
    const isOwnProfile = currentExpert.cognitoId === expertId;

    const handleUpdateProfileSubmit = async (values: Partial<FullProfile>): Promise<PutSuccessResponse> => {
        try {
            const { userType, otherTopics, ...rest } = { ...profile, ...values };

            const body = {
                presentations: otherTopics,
                ...rest,
            };

            await putProfile({
                patch: body,
            }).unwrap();

            if (putProfileError) {
                if (isAmplifyBaseQueryError(putProfileError)) {
                    return {
                        ok: false,
                        errorMessage: JSON.stringify(putProfileError.data || putProfileError.status),
                    };
                } else if (isErrorWithMessage(putProfileError)) {
                    return {
                        ok: false,
                        errorMessage: putProfileError.message,
                    };
                } else {
                    console.error(
                        "received an error during putProfile and it wasn't an AmplifyBaseQueryError or a SerializedError ",
                        putProfileError
                    );
                    return {
                        ok: false,
                        errorMessage: 'Oops, something went wrong. Try again shortly.',
                    };
                }
            }

            recordProfileEvent('Clicked Update', {
                category: 'Profile-Setup',
                'Clicked from Location': 'Profile Editor',
                'Profile Image': Boolean(profilePicture),
                'Professional Title': profile?.jobTitle || 'Unknown',
                Pronouns: profile?.pronouns || 'Unknown',
                Location: profile?.location || 'Unknown',
                Specialisms: profile?.otherTopics?.length || 'Unknown',
                changedValues: getChangedProfileValues(values, profile),
            });

            return {
                ok: true,
            };
        } catch (err: unknown) {
            let errorMessage = (err as Error).message;
            if (isAmplifyBaseQueryError(err) && typeof err.data === 'string') {
                errorMessage = err.data;
            }

            return {
                ok: false,
                errorMessage,
            };
        }
    };

    const handlePhotoSubmit = async (values: ProfilePhoto) => {
        try {
            const mimeType = values.type;
            recordProfileEvent('Saved Profile Picture', { mimeType });

            const { presignedPutUrl } = await putPhoto({
                mimeType,
            }).unwrap();

            if (presignedPutUrl) {
                const result = await axios.put(presignedPutUrl, values.blob, {
                    headers: {
                        'Content-Type': mimeType,
                        'x-amz-meta-cognito-id': currentExpert.cognitoId,
                    },
                });

                if (result.statusText !== 'OK') {
                    throw new Error('Failed to upload photo');
                }
            } else {
                throw new Error('No presigned url provided');
            }

            return {
                ok: true,
            };
        } catch (err: unknown) {
            let errorMessage = (err as Error).message;
            if (isAmplifyBaseQueryError(err) && typeof err.data === 'string') {
                errorMessage = err.data;
            }

            return {
                ok: false,
                errorMessage,
            };
        }
    };

    const handleSetIsEditOpen = (isOpen: boolean) => {
        if (isOpen === true) {
            recordProfileEvent('Clicked Edit Profile', {
                category: 'Profile-Setup',
                'Clicked from Location': 'Profile',
            });
        }
        setIsEditOpen(isOpen);
    };

    if (errorLoadingProfilePhoto) {
        //TODO: should we record this in amplitude or datadog?
        profilePicture = undefined;
    }

    return (
        <FlexBox minHeight="100%" backgroundColor={Colors.MemberGreen600} justifyContent="center">
            <div className="fixed -top-20 left-[-20%] right-[-20%] h-[708px] bg-[#0A392C] rounded-b-[100%]" />
            <FlexBox margin="62px 0px" zIndex={10} flexDirection="column" width="920px" gap="8px">
                <Toolbar isOwnProfile={isOwnProfile} setIsEditOpen={handleSetIsEditOpen} />

                <FlexBox gap="24px">
                    <FlexBox flexDirection="column" gap="16px" width="330px">
                        <BasicInfoTile
                            expertId={expertId}
                            externalId={connectionsProfile?.externalId}
                            profile={profile}
                            profilePictureUrl={profilePicture}
                            profilePicturePreview={profilePicturePreview}
                            isLoading={isLoadingProfiles}
                        />
                        <ConnectionsTile
                            following={connections?.following || []}
                            isLoading={isLoadingConnections}
                            isOwnProfile={isOwnProfile}
                        />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="16px" width="566px">
                        <AboutMeTile
                            aboutMe={profile?.aboutMe}
                            isLoading={isLoadingProfiles}
                            isOwnProfile={isOwnProfile}
                        />
                        <TopSpecialitiesTile
                            topSpecialisms={profile?.otherTopics}
                            isLoading={isLoadingProfiles}
                            isOwnProfile={isOwnProfile}
                        />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
            {!!profile && (
                <EditModal
                    isOpen={isEditOpen}
                    profile={profile}
                    profilePictureUrl={profilePicturePreview || profilePicture}
                    setProfilePicturePreview={setProfilePicturePreview}
                    handleSubmit={handleUpdateProfileSubmit}
                    onClose={() => {
                        handleSetIsEditOpen(false);
                        recordProfileEvent('Clicked Close', {
                            path: location.pathname,
                            category: 'Profile-Setup',
                            'Clicked from Location': 'Profile Editor',
                        });
                    }}
                    handlePhotoSubmit={handlePhotoSubmit}
                />
            )}
        </FlexBox>
    );
}
